import { useAuth, type Database } from '@eg/compartido'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { DatosFiscales, DatosSanitarios, DatosSucursal } from './esquemas'

type Tablas = Database['public']['Tables']
export type Empresa = Tablas['empresas']['Row']
export type Direccion = Tablas['empresa_direcciones']['Row']
export type Documento = Tablas['empresa_documentos']['Row']
export type TipoDocumento = Database['public']['Enums']['tipo_documento']

export type MiEmpresa = { empresa: Empresa; direcciones: Direccion[]; documentos: Documento[] }

export const TIPOS_PERMITIDOS = ['application/pdf', 'image/jpeg', 'image/png']
export const PESO_MAXIMO = 10 * 1024 * 1024

/** Primera empresa de la que la persona es miembro, con sus sucursales y documentos. null si no tiene. */
export function useMiEmpresa() {
  const { cliente, sesion } = useAuth()
  const uid = sesion?.user.id
  return useQuery({
    queryKey: ['mi-empresa', uid],
    enabled: Boolean(cliente && uid),
    queryFn: async (): Promise<MiEmpresa | null> => {
      const { data: miembro, error } = await cliente!
        .from('empresa_miembros')
        .select('empresa:empresas(*)')
        .eq('user_id', uid!)
        .order('creado_en')
        .limit(1)
        .maybeSingle()
      if (error) throw error
      const empresa = miembro?.empresa
      if (!empresa) return null
      const [dir, doc] = await Promise.all([
        cliente!.from('empresa_direcciones').select('*').eq('empresa_id', empresa.id).order('creado_en'),
        cliente!.from('empresa_documentos').select('*').eq('empresa_id', empresa.id).order('creado_en'),
      ])
      if (dir.error) throw dir.error
      if (doc.error) throw doc.error
      return { empresa, direcciones: dir.data, documentos: doc.data }
    },
  })
}

export class ErrorRegistro extends Error {
  constructor(public codigo: 'rfc_repetido' | 'maximo' | 'otro') {
    super(codigo)
  }
}

/** Acciones de cada paso del registro. Todas refrescan «mi empresa» al terminar. */
export function useAccionesRegistro() {
  const { cliente } = useAuth()
  const qc = useQueryClient()
  const refrescar = () => qc.invalidateQueries({ queryKey: ['mi-empresa'] })

  const registrar = useMutation({
    mutationFn: async (d: DatosFiscales) => {
      const { error } = await cliente!.rpc('registrar_empresa', {
        p_razon_social: d.razon_social,
        p_rfc: d.rfc,
        p_tipo: d.tipo,
        p_codigo_postal_fiscal: d.codigo_postal_fiscal,
        p_email_facturacion: d.email_facturacion,
        p_telefono: d.telefono,
      })
      if (error) {
        if (error.code === '23505') throw new ErrorRegistro('rfc_repetido')
        if (error.message.includes('máximo')) throw new ErrorRegistro('maximo')
        throw new ErrorRegistro('otro')
      }
    },
    onSuccess: refrescar,
  })

  const actualizarFiscal = useMutation({
    mutationFn: async ({ id, d }: { id: string; d: DatosFiscales }) => {
      const { error } = await cliente!
        .from('empresas')
        .update({ codigo_postal_fiscal: d.codigo_postal_fiscal, email_facturacion: d.email_facturacion, telefono: d.telefono ?? null })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: refrescar,
  })

  const guardarSanitario = useMutation({
    mutationFn: async ({ id, d }: { id: string; d: DatosSanitarios }) => {
      const { error } = await cliente!
        .from('empresas')
        .update({
          aviso_funcionamiento: d.aviso_funcionamiento ?? null,
          licencia_sanitaria: d.licencia_sanitaria ?? null,
          responsable_sanitario: d.responsable_sanitario,
          cedula_responsable: d.cedula_responsable,
        })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: refrescar,
  })

  const guardarSucursal = useMutation({
    mutationFn: async ({ id, existente, d }: { id: string; existente?: string; d: DatosSucursal }) => {
      const fila = { ...d, numero_interior: d.numero_interior ?? null, horario: d.horario ?? null, referencias: d.referencias ?? null }
      const { error } = existente
        ? await cliente!.from('empresa_direcciones').update(fila).eq('id', existente)
        : await cliente!.from('empresa_direcciones').insert({ ...fila, empresa_id: id, predeterminada: true })
      if (error) throw error
    },
    onSuccess: refrescar,
  })

  const subirDocumento = useMutation({
    mutationFn: async ({ id, tipo, archivo }: { id: string; tipo: TipoDocumento; archivo: File }) => {
      // La carpeta es el id de la empresa: así lo exigen la política de Storage y la restricción de la tabla.
      const limpio = archivo.name.normalize('NFD').replace(/[^\w.-]+/g, '_').slice(-80)
      const ruta = `${id}/${crypto.randomUUID()}-${limpio}`
      const { error: errorArchivo } = await cliente!.storage
        .from('documentos-empresa')
        .upload(ruta, archivo, { contentType: archivo.type, upsert: false })
      if (errorArchivo) throw errorArchivo
      const { error } = await cliente!.from('empresa_documentos').insert({ empresa_id: id, tipo, ruta_storage: ruta })
      if (error) throw error
    },
    onSuccess: refrescar,
  })

  return { registrar, actualizarFiscal, guardarSanitario, guardarSucursal, subirDocumento }
}

/** Primer paso con información pendiente, para continuar el registro donde se dejó. */
export function pasoInicial(m: MiEmpresa | null | undefined): number {
  if (!m) return 1
  const e = m.empresa
  if (!e.responsable_sanitario || !e.cedula_responsable || !(e.aviso_funcionamiento || e.licencia_sanitaria)) return 2
  if (m.direcciones.length === 0) return 3
  return 4
}

export function documentosCompletos(documentos: Documento[]): boolean {
  const tipos = new Set(documentos.map((d) => d.tipo))
  return tipos.has('constancia_situacion_fiscal') && tipos.has('cedula_responsable')
}
